import { Outlet, Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu, X, Instagram, Facebook, Youtube } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '@/lib/api';

export default function PublicLayout() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [settings, setSettings] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  useEffect(() => {
    api.get('/public/settings').then(res => setSettings(res.data)).catch(console.error);
  }, []);

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/portfolio', label: 'Portfolio' },
    { href: '/servizi', label: 'Servizi' },
    { href: '/chi-sono', label: 'Chi Sono' },
    { href: '/recensioni', label: 'Recensioni' },
    { href: '/contatti', label: 'Contatti' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? 'bg-background/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
        }`}
      >
        <nav className="max-w-[1800px] mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex flex-col" data-testid="logo-link">
              <span className="font-heading text-xl font-semibold tracking-tight">Andrea Caruso</span>
              <span className="text-xs text-muted-foreground tracking-widest uppercase">Fotografo</span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-10">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                  className={`text-sm tracking-wide link-underline transition-colors ${
                    isActive(link.href) ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <Link
                to="/prenota"
                data-testid="header-cta-btn"
                className="inline-flex items-center justify-center px-6 py-2.5 bg-foreground text-background text-sm font-medium tracking-wide hover:bg-foreground/90 transition-colors"
              >
                Prenota
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2"
              data-testid="mobile-menu-btn"
              aria-label="Menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-background pt-20 lg:hidden"
          >
            <nav className="flex flex-col items-center gap-8 py-12">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                  className={`text-2xl font-heading ${
                    isActive(link.href) ? 'text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/prenota"
                data-testid="mobile-cta-btn"
                className="mt-4 inline-flex items-center justify-center px-8 py-3 bg-foreground text-background text-lg font-medium"
              >
                Prenota
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-foreground text-background py-16 lg:py-24">
        <div className="max-w-[1800px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
            {/* Brand */}
            <div className="lg:col-span-2">
              <h3 className="font-heading text-3xl mb-4">{settings?.hero_title || "Andrea Caruso"}</h3>
              <p className="text-background/70 max-w-md font-accent text-lg italic">
                {settings?.hero_tagline || "Catturare l'essenza, raccontare l'emozione."}
              </p>
              {settings?.footer?.additional_text && (
                <p className="text-background/50 text-sm mt-4">{settings.footer.additional_text}</p>
              )}
            </div>

            {/* Quick Links */}
            {settings?.footer?.show_quick_links !== false && (
              <div>
                <h4 className="text-sm font-medium tracking-widest uppercase mb-6 text-background/50">Navigazione</h4>
                <nav className="flex flex-col gap-3">
                  {settings?.footer?.quick_links?.length > 0 ? (
                    <>
                      {settings.footer.quick_links.map((link, idx) => (
                        <Link
                          key={idx}
                          to={link.url}
                          className="text-background/70 hover:text-background transition-colors"
                        >
                          {link.label}
                        </Link>
                      ))}
                      <Link
                        to="/gestisci-prenotazione"
                        className="text-background/70 hover:text-background transition-colors"
                        data-testid="footer-manage-booking-link"
                      >
                        Gestisci Prenotazione
                      </Link>
                    </>
                  ) : (
                    <>
                      {navLinks.map((link) => (
                        <Link
                          key={link.href}
                          to={link.href}
                          className="text-background/70 hover:text-background transition-colors"
                        >
                          {link.label}
                        </Link>
                      ))}
                      <Link
                        to="/gestisci-prenotazione"
                        className="text-background/70 hover:text-background transition-colors"
                        data-testid="footer-manage-booking-link"
                      >
                        Gestisci Prenotazione
                      </Link>
                    </>
                  )}
                </nav>
              </div>
            )}

            {/* Contact */}
            {settings?.footer?.show_contact_info !== false && (
              <div>
                <h4 className="text-sm font-medium tracking-widest uppercase mb-6 text-background/50">Contatti</h4>
                <div className="flex flex-col gap-3 text-background/70">
                  {settings?.contact_email && <a href={`mailto:${settings.contact_email}`} className="hover:text-background">{settings.contact_email}</a>}
                  {settings?.contact_phone && <a href={`tel:${settings.contact_phone}`} className="hover:text-background">{settings.contact_phone}</a>}
                  {settings?.contact_address && <p className="text-background/60 text-sm">{settings.contact_address}</p>}
                </div>
                {settings?.footer?.show_social !== false && (
                  <div className="flex gap-4 mt-6">
                    {settings?.social_instagram && (
                      <a href={settings.social_instagram} target="_blank" rel="noopener noreferrer" className="text-background/70 hover:text-background" aria-label="Instagram">
                        <Instagram size={20} />
                      </a>
                    )}
                    {settings?.social_facebook && (
                      <a href={settings.social_facebook} target="_blank" rel="noopener noreferrer" className="text-background/70 hover:text-background" aria-label="Facebook">
                        <Facebook size={20} />
                      </a>
                    )}
                    {settings?.social_youtube && (
                      <a href={settings.social_youtube} target="_blank" rel="noopener noreferrer" className="text-background/70 hover:text-background" aria-label="YouTube">
                        <Youtube size={20} />
                      </a>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="border-t border-background/10 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-background/50">
              {(settings?.footer?.copyright_text || '© {year} Andrea Caruso. Tutti i diritti riservati.').replace('{year}', new Date().getFullYear())}
            </p>
            <div className="flex gap-6 text-sm text-background/50">
              <Link to="/buono-regalo" className="hover:text-background" data-testid="footer-gift-link">Buono Regalo</Link>
              <Link to="/privacy" className="hover:text-background">Privacy Policy</Link>
              <Link to="/faq" className="hover:text-background">FAQ</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
